import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaWallet,
  FaBolt,
  FaCommentDots,
  FaChartLine,
} from 'react-icons/fa';
import { Button } from '../../components';
import logo from '../../assets/klyx-logo1.png';
import './Creators.scss';

const CreatorsPage: React.FC = () => {
  const navigate = useNavigate();

  // Land at the top on route in — same pattern as /about and
  // /how-it-works. Users arrive here from the /home audience-split
  // CTA which may be halfway down the page.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  return (
    <div className="creators-page">
      <div className="creators-container">
        {/* Hero — creator-only pitch. Refactor doc §5.1. */}
        <header className="creators-header">
          <img src={logo} alt="Klyx" className="creators-header__logo" />
          <h1>Your work. Your audience. Yours to keep.</h1>
          <p className="creators-header__tagline">
            Klyx gives you a profile you actually own, monetization from day
            one, and reputation that compounds no matter where you post.
            Built for creators tired of renting from platforms.
          </p>
          <div className="creators-header__cta">
            <Button variant="primary" size="large" onClick={() => navigate('/signup')}>
              Start earning →
            </Button>
          </div>
        </header>

        {/* §5.2 — What you get. Four tiles covering the concrete
            creator-side value props. Refactor doc §5.2. */}
        <section className="creators-section">
          <h2>What you get on Klyx</h2>
          <div className="creators-tiles">
            <div className="creators-tile">
              <FaWallet className="creators-tile__icon" aria-hidden />
              <h3>A profile you actually own</h3>
              <p>
                Your profile is anchored to your Klever wallet, not a
                platform-owned account. Every post, every subscriber, every
                follower — attached to something no app can revoke.
              </p>
            </div>
            <div className="creators-tile">
              <FaBolt className="creators-tile__icon" aria-hidden />
              <h3>Monetization from day one</h3>
              <p>
                No follower threshold. No verification queue. Turn on paid
                posts, subscriptions, or per-post paywalls the moment you
                sign up.
              </p>
            </div>
            <div className="creators-tile">
              <FaCommentDots className="creators-tile__icon" aria-hidden />
              <h3>Direct connection to your audience</h3>
              <p>
                Fans can DM you directly. No algorithm gating your
                notifications. No creator-inbox hell built to sell you a
                paid tier out of it.
              </p>
            </div>
            <div className="creators-tile">
              <FaChartLine className="creators-tile__icon" aria-hidden />
              <h3>Discovery ranked by reputation</h3>
              <p>
                New creators aren&apos;t invisible on Klyx. Discovery ranks by
                reputation — real work, real engagement — not raw follower
                count. Small accounts with good work outrank dormant giants.
              </p>
            </div>
          </div>
        </section>

        {/* §5.3 — Show me the money. Three tiles with the actual
            on-chain fee splits. No named-competitor comparisons —
            the numbers speak for themselves. Refactor doc §5.3. */}
        <section className="creators-section">
          <h2>Show me the money</h2>
          <p className="creators-section__lede">
            Creators keep more on Klyx than on any platform you&apos;re used
            to. The splits below are on-chain — every payment settles in
            your Klever wallet automatically. No monthly minimums. No
            payout thresholds. No mystery deductions.
          </p>
          <div className="creators-money">
            <div className="creators-money__tile">
              <div className="creators-money__value">95%</div>
              <h3>Paid posts</h3>
              <p>
                Fans pay per post, you keep 95%. The remaining 5% covers a
                3% profile-owner cut (usually you) and a 2% platform fee.
                No thresholds, no monthly minimums.
              </p>
            </div>
            <div className="creators-money__tile">
              <div className="creators-money__value">95%</div>
              <h3>Subscriptions</h3>
              <p>
                Recurring revenue for subscribers-only content. Same 95 / 3
                / 2 split as paid posts. Set your own price, set your own
                subscription period.
              </p>
            </div>
            <div className="creators-money__tile">
              <div className="creators-money__value">3%</div>
              <h3>Resale royalty — forever</h3>
              <p>
                If a fan buys your profile and later resells it, you earn
                3% of that resale. And every resale after that. Not a
                one-time bonus — a lifetime royalty on the market your
                identity creates.
              </p>
            </div>
            <div className="creators-money__tile">
              <div className="creators-money__value">5%</div>
              <h3>Post spotlight</h3>
              <p>
                Boost a single post into feed + discovery for a set
                duration. Anyone can buy it — including you. Whoever
                pays, you earn 5% of the spotlight fee as the profile
                owner. Primary win: reach the moment you need it.
              </p>
            </div>
            <div className="creators-money__tile">
              <div className="creators-money__value">5%</div>
              <h3>Profile spotlight</h3>
              <p>
                Amplify your whole profile across the platform for a set
                window. Fans who back you can buy this on your behalf.
                Same 5% owner cut. Same platform-wide reach — different
                surface.
              </p>
            </div>
          </div>
          <p className="creators-money__note">
            Full mechanics on{' '}
            <a href="/how-it-works">how it works</a>, including the
            always-for-sale profile-trading rules and the 10%
            minimum-price-increase that guarantees a resale always
            profits the seller.
          </p>
        </section>

        {/* §5.4 — Testimonials. Section commented out until we have
            3 real quotes (per refactor doc §10). The block below is
            the intended shape; uncomment when quotes land. */}
        {/*
        <section className="creators-section">
          <h2>What creators are saying</h2>
          <div className="creators-quotes">
            <blockquote className="creators-quote">
              <p>"&hellip;"</p>
              <cite>— Creator name, category</cite>
            </blockquote>
          </div>
        </section>
        */}

        {/* Final CTA — §5.5 */}
        <section className="creators-cta">
          <h2>Claim your creator profile.</h2>
          <p>Free to sign up. Set your prices anytime.</p>
          <div className="creators-cta__buttons">
            <Button variant="primary" size="large" onClick={() => navigate('/signup')}>
              Claim your creator profile →
            </Button>
            <Button variant="ghost" size="large" onClick={() => navigate('/how-it-works')}>
              Read the mechanics first
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CreatorsPage;
